import { ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { API } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { DateTime, Duration } from 'luxon';
import { XYChartModel } from '@ngx-seatback/charts';
import { UtilService } from '../../../../services/util.service';
import { ChartComponent } from '@ngx-seatback/charts';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'sb-player-time-data',
  templateUrl: './player-time-data.component.html',
  styleUrls: ['./player-time-data.component.scss'],
  standalone: false,
})
export class PlayerTimeDataComponent implements OnInit, OnDestroy {
  @ViewChild('chart') chart: ChartComponent;
  loading: boolean;
  // tslint:disable-next-line:variable-name
  _chartHeight: number;
  @Input() set chartHeight(val) {
    if (val) {
      this._chartHeight = val;
    } else {
      this._chartHeight = 150;
    }
  }

  get chartHeight() {
    return this._chartHeight;
  }

  @Input() resolveInCom: boolean;
  timespan: Duration;
  @Input() halfHeight: boolean;
  // tslint:disable-next-line:variable-name
  _inputData: any;
  @Input() set inputData(val) {
    this._inputData = val;
    if (!this.resolveInCom && this._inputData) {
      this.loading = true;
      this.cdRef.detectChanges();
      this.initChartData(this._inputData);
    }
  }

  get inputData() {
    return this._inputData;
  }

  // tslint:disable-next-line:variable-name
  _from?: SbDateTime;
  get from(): SbDateTime {
    return this._from;
  }

  @Input() set from(val) {
    this._from = val;
  }

  // tslint:disable-next-line:variable-name
  _till?: SbDateTime;
  get till(): SbDateTime {
    return this._till;
  }

  @Input() set till(val) {
    this._till = val;
  }

  playerTimeData: XYChartModel;
  @ViewChild('chartLegend', { read: ElementRef, static: false }) chartLegendContainer: ElementRef;

  subs: Subscription[] = [];

  constructor(
    private api: API,
    private errorHandler: ErrorService,
    public util: UtilService,
    private cdRef: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.playerTimeData = {
      data: [],
      height: 150,
      loadingDone: false,
      title: '',
      valueAssignments: [
        {
          value: 'online',
          valueName: 'Online',
          color: '#2cc361',
          categories: ['date'],
        },
      ],
      categoryType: 'date',
      tensionX: 1,
      legendContainer: this.chartLegendContainer,
      legendAlign: 'right',
      legendContainerWidth: '600px',
    };
    this.timespan = Duration.fromMillis(0);
  }

  ngOnInit(): void {
    if (this.resolveInCom) {
      this.refreshFromServer();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
  }

  refreshFromServer() {
    if (!this.from && !this.till) {
      this.from = new SbDateTime(DateTime.local().startOf('day').minus({ day: 91 }));
      this.till = new SbDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
    }
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
    const sub = this.api
      .ss_nm_Reporting_deviceStatusCountHistory({
        from: this.from,
        till: this.till,
        productive: true,
        interval: 604800,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.initChartData(data.buckets[0].frames);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  initChartData(data) {
    if (this.from && this.till) {
      this.timespan = DateTime.fromJSDate(this.util.localDateToJSDate(this.till)).diff(
        DateTime.fromJSDate(this.util.localDateToJSDate(this.from)),
        'days'
      );
    }
    const playerTimeChartData = [];

    for (const i of data) {
      playerTimeChartData.push({
        date: i.range.from.value,
        devices: i.data.total,
        online: i.data.quotaOnlineStatus_Online,
        currentlyOffline: i.data.quotaOnlineStatus_CurrentlyOffline,
        plannedOffline: i.data.quotaOnlineStatus_PlannedOffline,
        offline: i.data.quotaOnlineStatus_Offline,
        unknown: i.data.quotaOnlineStatus_Unknown,
      });
    }
    this.chart.chartData.legend = true;
    this.chart.chartData.chartLegendContainer = this.chartLegendContainer;
    this.chart.chartData.legendAlign = 'right';
    this.chart.chartData.legendContainerWidth = '600px';
    this.chart.chartData.loadingDone = true;
    this.playerTimeData = {
      title: 'Test',
      height: this.chartHeight,
      valueAssignments: [
        /*        {
                  value: 'devices',
                  valueName: 'Player',
                  color: '#016aa2',
                  categories: ['date'],
                },*/
        {
          value: 'online',
          valueName: 'Online',
          color: '#2cc361',
          categories: ['date'],
          fill: true,
        },
        {
          value: 'currentlyOffline',
          valueName: 'CurrentlyOffline',
          color: '#ffb822',
          categories: ['date'],
          fill: true,
        },
        {
          value: 'plannedOffline',
          valueName: 'PlannedOffline',
          color: '#ff7600',
          categories: ['date'],
          fill: true,
        },
        {
          value: 'offline',
          valueName: 'Offline',
          color: '#c36157',
          categories: ['date'],
          fill: true,
        },
        {
          value: 'unknown',
          valueName: 'Unknown',
          color: '#999dae',
          categories: ['date'],
          fill: true,
        },
      ],
      categoryType: 'date',
      legend: true,
      legendContainer: this.chartLegendContainer,
      legendAlign: 'right',
      legendContainerWidth: '600px',
      loadingDone: true,
      tensionX: 1,
      fill: true,
      stacked: true,
      showTotalInTooltip: true,
      data: playerTimeChartData,
    };
    this.loading = false;
    this.chart.rerenderChart(this.playerTimeData);
    this.cdRef.detectChanges();
  }
}
